import { DATE_RANGES, type DateRangeValue } from "@finda/shared/constants";
import type { TimeOfDay } from "@finda/shared/utils";

export interface JukeboxLaunch {
  eventId: number | null;
  artistId: number | null;
}

function positiveId(value: string | null): number | null {
  if (!value || !/^[1-9]\d*$/.test(value)) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) ? id : null;
}

export function parseJukeboxLaunch(params: Pick<URLSearchParams, "get">): JukeboxLaunch | null {
  const enabled = params.get("jukebox");
  if (enabled === null || !["", "1", "true"].includes(enabled)) return null;
  return { eventId: positiveId(params.get("event")), artistId: positiveId(params.get("artist")) };
}

export function jukeboxHref(playlistHref: string, eventId?: number, artistId?: number): string {
  const url = new URL(playlistHref, "https://www.finda.show");
  url.hash = "";
  url.searchParams.set("jukebox", "1");
  url.searchParams.delete("event");
  url.searchParams.delete("artist");
  if (eventId !== undefined) url.searchParams.set("event", String(eventId));
  if (artistId !== undefined) url.searchParams.set("artist", String(artistId));
  return `${url.pathname}${url.search}`;
}

interface PlaylistEntry {
  event: { id: number };
  artists: { attractionId: number }[];
}

export interface JukeboxStart<T> {
  playlist: T[];
  index: number;
  artistId?: number;
}

/** Preserve the shared act/bill, then continue through the current playlist. */
export function resolveJukeboxStart<T extends PlaylistEntry>(playlist: T[], source: T[], launch: JukeboxLaunch, restoredTarget?: T | null): JukeboxStart<T> {
  const exactTarget = source.find((entry) => entry.event.id === launch.eventId);
  const restoredMatches = restoredTarget && (launch.eventId !== null
    ? restoredTarget.event.id === launch.eventId
    : restoredTarget.artists.some((artist) => artist.attractionId === launch.artistId));
  if (!exactTarget && restoredMatches) {
    const artistId = restoredTarget.artists.some((artist) => artist.attractionId === launch.artistId) ? launch.artistId! : undefined;
    return { playlist: [restoredTarget, ...playlist.filter((entry) => entry.event.id !== restoredTarget.event.id)], index: 0, artistId };
  }
  const targetId = exactTarget?.event.id ?? (launch.artistId
    ? source.find((entry) => entry.artists.some((artist) => artist.attractionId === launch.artistId))?.event.id
    : launch.eventId);
  let result = playlist;
  let index = playlist.findIndex((entry) => entry.event.id === targetId);
  const sourceIndex = source.findIndex((entry) => entry.event.id === targetId);
  const target = source[sourceIndex];
  if (target) {
    if (index < 0) {
      const positions = new Map(source.map((entry, i) => [entry.event.id, i]));
      index = playlist.findIndex((entry) => (positions.get(entry.event.id) ?? -1) > sourceIndex);
      if (index < 0) index = playlist.length;
      result = [...playlist.slice(0, index), target, ...playlist.slice(index)];
    } else if (launch.artistId && !playlist[index].artists.some((artist) => artist.attractionId === launch.artistId)
      && target.artists.some((artist) => artist.attractionId === launch.artistId)) {
      result = playlist.map((entry, i) => i === index ? target : entry);
    }
  }
  // Unavailable or malformed targets still open the current playlist.
  const matchedEvent = index >= 0;
  if (!matchedEvent) index = 0;
  const artistId = matchedEvent && result[index]?.artists.some((artist) => artist.attractionId === launch.artistId)
    ? launch.artistId! : undefined;
  return { playlist: result, index, artistId };
}

export interface CityJukeboxFilters {
  selectedGenres: string[];
  genreMatchMode: "or" | "and";
  dateRange: DateRangeValue;
  customStartDate: string | null;
  customEndDate: string | null;
  selectedTime: TimeOfDay | null;
  selectedNearbySlugs: string[];
}

export function parseCityJukeboxFilters(params: URLSearchParams): CityJukeboxFilters {
  const date = params.get("date");
  const time = params.get("time");
  const dateValue = (key: string) => {
    const value = params.get(key);
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value ? value : null;
  };
  return {
    selectedGenres: [...new Set(params.getAll("genre").map((genre) => genre.trim()).filter(Boolean))],
    genreMatchMode: params.get("match") === "and" ? "and" : "or",
    dateRange: DATE_RANGES.find((range) => range.value === date)?.value ?? "all",
    customStartDate: dateValue("from"),
    customEndDate: dateValue("to"),
    selectedTime: time === "morning" || time === "afternoon" || time === "evening" ? time : null,
    selectedNearbySlugs: [...new Set(params.getAll("nearby"))],
  };
}

export function cityJukeboxPlaylistHref(path: string, filters: CityJukeboxFilters): string {
  const query = new URLSearchParams();
  for (const genre of filters.selectedGenres) query.append("genre", genre);
  if (filters.genreMatchMode === "and" && filters.selectedGenres.length > 1) query.set("match", "and");
  if (filters.dateRange !== "all") query.set("date", filters.dateRange);
  if (filters.dateRange === "custom") {
    if (filters.customStartDate) query.set("from", filters.customStartDate);
    if (filters.customEndDate) query.set("to", filters.customEndDate);
  }
  if (filters.selectedTime) query.set("time", filters.selectedTime);
  for (const slug of filters.selectedNearbySlugs) query.append("nearby", slug);
  return query.size ? `${path}?${query}` : path;
}
